import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFlagCheckered,
  faTrophy,
  faStopwatch,
  faDumbbell,
  faLayerGroup,
  faScaleBalanced,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { themeColors, getCssVar } from "../utils/theme";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useTheme } from "../hooks/useTheme";
import { useLogoSrc } from "../context/LogoContext";

// Keyframes injetados UMA vez — partículas lêem via CSS custom props
// Arco físico: sobe levemente nos primeiros 25% depois cai com gravidade
const CONFETTI_CSS = `
@keyframes cf-burst-arc {
  0%   {
    transform: translate(-50%, -50%) scale(0.3) rotate(0deg);
    opacity: 0;
    animation-timing-function: cubic-bezier(0.11, 0.85, 0.3, 1);
  }
  5%   { opacity: 1; }
  22%  {
    transform: translate(calc(-50% + var(--cf-bx)), calc(-50% + var(--cf-by))) scale(1) rotate(calc(var(--cf-rot) * 0.3));
    opacity: 1;
    animation-timing-function: cubic-bezier(0.45, 0, 0.82, 0.4);
  }
  88%  { opacity: 0.75; }
  100% {
    transform: translate(calc(-50% + var(--cf-bx) + var(--cf-tx)), calc(-50% + var(--cf-by) + 560px)) rotate(var(--cf-rot));
    opacity: 0;
  }
}
@keyframes cf-shock {
  0%   { transform: translate(-50%, -50%) scale(0.2); opacity: 0.6; }
  55%  { opacity: 0.22; }
  100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
}
@keyframes summary-backdrop-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes summary-card-in {
  from { opacity: 0; transform: scale(0.86) translateY(32px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
}
@keyframes summary-stat-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

const DEFAULT_LOGO_FOR_DARK_THEME = "/brand/treinonexo-wordmark-white.svg"; // branco para tema escuro
const DEFAULT_LOGO_FOR_LIGHT_THEME = "/brand/treinonexo-wordmark.svg"; // escuro para tema claro

// Exercícios visíveis com a lista recolhida. O resumo é printado e postado
// nos stories: com a lista inteira (9+ exercícios) ela tomava mais da metade
// da imagem e saía cortada, competindo com os números do treino.
const COLLAPSED_EXERCISES = 3;

/** 45s, 32min, 1h47min - legível num print, sem segundos depois de 1 min. */
export function formatSummaryDuration(totalSecs) {
  const secs = Math.max(0, Math.round(totalSecs || 0));
  if (secs < 60) return `${secs}s`;
  const totalMin = Math.floor(secs / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${totalMin}min`;
  return m > 0 ? `${h}h${String(m).padStart(2, "0")}min` : `${h}h`;
}

/** 850 -> "850 kg"; 6780 -> "6,8 t" (igual ao gráfico de carga); 0 -> "-". */
export function formatSummaryVolume(kg) {
  if (!kg || kg <= 0) return "-";
  if (kg >= 1000) return `${(kg / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} t`;
  return `${Math.round(kg)} kg`;
}

/** Variação vs. a última vez do mesmo treino: { pct, label } ou null. */
export function volumeVsPrevious(current, previous) {
  if (!current || current <= 0 || !previous || previous <= 0) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return { pct, label: "= último treino" };
  return { pct, label: `${pct > 0 ? "▲" : "▼"} ${Math.abs(pct)}% vs último` };
}

// Faixa da semana (seg a dom): os dias "pagos" que o aluno gosta de postar.
// Sem caixa nem borda, pra não virar mais um bloco no print: só as bolinhas
// e uma legenda. Dia sem treino em contorno apagado (nunca vermelho: é o
// resumo que o aluno posta, não uma cobrança). Hoje ganha um anel.
const WEEK_LABELS = ["S", "T", "Q", "Q", "S", "S", "D"];
const WEEK_NAMES = ["segunda", "terça", "quarta", "quinta", "sexta", "sábado", "domingo"];

export function weekCaption(count, goal) {
  if (goal && count >= goal) return `Meta da semana batida! ${count} de ${goal}`;
  if (goal) return `${count} de ${goal} treinos da semana`;
  return `${count} ${count === 1 ? "treino" : "treinos"} nesta semana`;
}

function WeekStrip({ week, blue, blueOn, accentRgb }) {
  if (!week || !Array.isArray(week.days) || week.days.length !== 7) return null;
  const count = week.days.filter(Boolean).length;
  const trainedNames = week.days.map((d, i) => (d ? WEEK_NAMES[i] : null)).filter(Boolean);
  const goalHit = week.weekly_goal && count >= week.weekly_goal;
  return (
    <div
      role="img"
      aria-label={`Semana: treinou ${trainedNames.join(", ")}. ${weekCaption(count, week.weekly_goal)}.`}
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}
    >
      <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
        {week.days.map((done, i) => {
          const isToday = i === week.today_index;
          return (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <span
                style={{
                  width: 28, height: 28, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12,
                  background: done ? blue : "transparent",
                  color: done ? blueOn : "transparent",
                  border: done ? `1.5px solid ${blue}` : "1.5px solid var(--border-0-15)",
                  boxShadow: isToday ? `0 0 0 3px rgba(${accentRgb},0.30)` : "none",
                }}
              >
                {done && <FontAwesomeIcon icon={faCheck} aria-hidden="true" />}
              </span>
              <span style={{
                fontSize: 11, fontWeight: 700,
                color: isToday ? "var(--text-primary)" : "var(--text-0-50)",
              }}>
                {WEEK_LABELS[i]}
              </span>
            </div>
          );
        })}
      </div>
      {/* Sem legenda de contagem: as bolinhas já mostram os dias. Só a meta
          batida ganha texto, que é o momento de comemorar no print. */}
      {goalHit && (
        <div style={{ fontSize: 12, fontWeight: 800, color: blue }}>Meta da semana batida!</div>
      )}
    </div>
  );
}

export default function WorkoutSummaryModal({ summary, onClose }) {
  useBodyScrollLock(!!summary);
  const { isDark, isLight } = useTheme();
  // Mesmo mecanismo de ModalHeader.jsx (R248): logo custom do professor via
  // contexto, variante clara OPCIONAL trocada pelo tema, fallback pra dark
  // quando não houver light (comportamento de sempre, sem regressão).
  const { dark: ctxLogoDark, light: ctxLogoLight } = useLogoSrc() || {};
  const customLogoSrc = isLight
    ? (ctxLogoLight || ctxLogoDark)
    : (ctxLogoDark || ctxLogoLight);

  // Partículas: 3 formas × 4 cores — geradas uma vez por abertura.
  // Explosão: ângulo restrito ao hemisfério superior (180°-360°) pra estourar
  // pra cima/pros lados, nunca pra baixo — a queda (fase 2 do keyframe) assume depois.
  const particles = useMemo(() => {
    // Formas: fita fina, quadrado, ponto — variam a textura visual
    const shapes = [
      { w: 3,  h: 11, r: "1px"  }, // fita (mais frequente)
      { w: 3,  h: 11, r: "1px"  },
      { w: 6,  h: 6,  r: "2px"  }, // quadradinho
      { w: 5,  h: 5,  r: "50%"  }, // ponto
    ];
    // Índices de cor: acento dominante, dourado, pérola (distribuídos 40/35/25)
    const colorMap = [0, 0, 0, 1, 1, 1, 2, 2, 3];
    return Array.from({ length: 72 }, (_, i) => {
      const sh = shapes[i % shapes.length];
      const angle = Math.PI + Math.random() * Math.PI; // 180°-360° (leque pra cima)
      const dist  = 55 + Math.random() * 130;           // alcance do estouro
      return {
        bx:       Math.cos(angle) * dist, // deslocamento X da explosão
        by:       Math.sin(angle) * dist, // deslocamento Y da explosão (sempre <= 0)
        w:        sh.w,
        h:        sh.h + Math.random() * 4,
        round:    sh.r,
        colorIdx: colorMap[i % colorMap.length],
        duration: 2.0 + (i % 7) * 0.30,
        delay:    (i % 24) * 0.012, // quase simultâneo — precisa parecer explosão, não gotejar
        tx:       (Math.random() - 0.5) * 150,
        rot:      Math.random() * 660 - 330,
      };
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [showAllExercises, setShowAllExercises] = useState(false);
  useEffect(() => { setShowAllExercises(false); }, [summary]);

  useEffect(() => {
    if (!summary) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [summary, onClose]);

  if (!summary) return null;

  const { durationSecs, totalExercises, totalSets, totalVolume, exercises, pointsAwarded, volumeBonus, personalRecords, workoutName, finishedAt, previousVolume, week } = summary;
  const volumeTrend = volumeVsPrevious(totalVolume, previousVolume);
  const durStr = formatSummaryDuration(durationSecs);
  const dateStr = new Date(finishedAt || Date.now()).toLocaleDateString("pt-BR", { day: "numeric", month: "long" });

  // Exercício sem série registrada não entra: num post, "0 séries" vira um
  // registro público de exercício pulado.
  //
  // Recordes pessoais entram na própria lista (troféu + valor dourado), não
  // num bloco à parte: o bloco laranja repetia a notícia do topo e o mesmo
  // exercício aparecia duas vezes no print. Os de recorde vêm primeiro e
  // ficam sempre visíveis; recolhida, o resto completa com os de maior
  // volume até COLLAPSED_EXERCISES; aberta, todos na ordem do treino.
  const records = Array.isArray(personalRecords) ? personalRecords : [];
  const doneExercises = (exercises || []).filter((ex) => ex.sets > 0);
  const takenRecords = new Set();
  const findRecord = (ex) => {
    const pr = records.find((r, i) => !takenRecords.has(i) && (
      (ex.exerciseItemId != null && r.exercise_item_id === ex.exerciseItemId) ||
      r.exercise_name === ex.name
    ));
    if (pr) takenRecords.add(records.indexOf(pr));
    return pr || null;
  };
  const rows = doneExercises.map((ex) => ({ ...ex, pr: findRecord(ex) }));
  // Recorde de exercício que não está na lista (não deveria acontecer): entra mesmo assim
  records.forEach((r, i) => {
    if (!takenRecords.has(i)) rows.push({ name: r.exercise_name, sets: null, volume: 0, pr: r });
  });
  const prRows = rows
    .filter((r) => r.pr)
    .sort((a, b) => records.indexOf(a.pr) - records.indexOf(b.pr));
  const otherRows = rows.filter((r) => !r.pr);
  const otherSlots = Math.max(0, COLLAPSED_EXERCISES - prRows.length);
  const hiddenCount = Math.max(0, otherRows.length - otherSlots);
  const visibleOthers = showAllExercises || hiddenCount === 0
    ? otherRows
    : otherRows
        .map((ex, i) => ({ ex, i }))
        .sort((a, b) => (b.ex.volume - a.ex.volume) || (a.i - b.i))
        .slice(0, otherSlots)
        .sort((a, b) => a.i - b.i)
        .map(({ ex }) => ex);
  const visibleExercises = [...prRows, ...visibleOthers];

  const fmtKg = (n) => Number(n).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
  const recordsLabel = records.length > 0
    ? (records.length === 1 ? "1 recorde" : `${records.length} recordes`)
    : (volumeBonus ? "Recorde de volume" : null);

  const accentRgb  = getCssVar("--accent-rgb", "29, 158, 117");
  const blue       = themeColors.blue;
  const blueHover  = themeColors.blueHover;
  const blueOn     = getCssVar("--blue-on", "var(--text-primary)");
  const activeLogo = customLogoSrc || (isDark ? DEFAULT_LOGO_FOR_DARK_THEME : DEFAULT_LOGO_FOR_LIGHT_THEME);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return createPortal(
    <div key={`modal-theme-${isDark}`}
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: "rgba(10,18,35,0.68)",
        WebkitBackdropFilter: "blur(8px)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
        overflowY: "auto",
        animation: "summary-backdrop-in 0.22s ease both",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "linear-gradient(160deg, var(--surface-0-98) 0%, var(--surface-26-85) 100%)",
          border: "1px solid rgba(" + accentRgb + ",0.3)",
          borderRadius: 20,
          overflow: "hidden",
          maxWidth: 360,
          width: "100%",
          maxHeight: "calc(100vh - 32px)",
          display: "flex", flexDirection: "column",
          boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
          margin: "auto",
          position: "relative",
          animation: "summary-card-in 0.38s cubic-bezier(0.34,1.56,0.64,1) both",
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Treino concluído"
      >
        {/* Celebração: explosão (estouro radial) + queda de confete com gravidade */}
        {!prefersReducedMotion && (
          <div style={{
            position: "absolute", top: -70, left: 0, right: 0, height: 560,
            overflow: "hidden", pointerEvents: "none", zIndex: 10,
          }}>
            <style>{CONFETTI_CSS}</style>
            {/* Flash do estouro: brilho radial que expande e some rápido, no instante da explosão */}
            <div style={{
              position: "absolute",
              left: "50%",
              top: 128,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: `radial-gradient(circle, rgba(${accentRgb},0.55) 0%, rgba(${accentRgb},0.16) 45%, rgba(${accentRgb},0) 72%)`,
              animation: "cf-shock 0.46s cubic-bezier(0.15,0.8,0.4,1) both",
            }} />
            {(() => {
              // 4 cores: acento, acento-claro, dourado, pérola
              const colors = [
                `rgba(${accentRgb}, 1)`,
                `rgba(${accentRgb}, 0.52)`,
                "#f59e0b",
                "#dde8f0",
              ];
              return particles.map((p, i) => (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: 128,
                    width: p.w,
                    height: p.h,
                    borderRadius: p.round,
                    background: colors[p.colorIdx],
                    animation: `cf-burst-arc ${p.duration}s ${p.delay}s both`,
                    willChange: "transform, opacity",
                    "--cf-bx":  `${p.bx}px`,
                    "--cf-by":  `${p.by}px`,
                    "--cf-tx":  `${p.tx}px`,
                    "--cf-rot": `${p.rot}deg`,
                  }}
                />
              ));
            })()}
          </div>
        )}
        {/* Marca centralizada e sem o X: o resumo é printado e postado nos
            stories, e o canto superior esquerdo fica sob o avatar do
            Instagram (a marca sumia) e o X aparecia na imagem. Fechar fica
            no botão do fim, no clique fora e no Esc. */}
        <div style={{
          display: "flex", justifyContent: "center",
          padding: "18px 20px 4px",
          flexShrink: 0,
        }}>
          <img
            src={activeLogo}
            alt="Logo"
            draggable={false}
            // Caixa FIXA nos dois eixos (mesma solução de ModalHeader.module.css):
            // qualquer proporção de logo ocupa o mesmo espaço.
            style={customLogoSrc
              ? { width: 240, height: 56, objectFit: "contain", objectPosition: "center", opacity: 0.98 }
              : { width: 170, height: 34, objectFit: "contain", objectPosition: "center", opacity: 0.95 }
            }
          />
        </div>

        {/* Conteudo com scroll se necessario */}
        <div style={{ overflowY: "auto", padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Título */}
          <div style={{ textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 22, fontWeight: 800, color: "var(--text-primary)" }}>
              <FontAwesomeIcon icon={faFlagCheckered} style={{ fontSize: 22, color: blue }} aria-hidden="true" />
              Treino concluído!
            </div>
            <div style={{ fontSize: 13, color: "var(--text-0-60)", fontWeight: 600, marginTop: 6 }}>
              {workoutName ? `${workoutName} · ${dateStr}` : dateStr}
            </div>
            {(pointsAwarded > 0 || recordsLabel) && (
              <div style={{ fontSize: 13, color: "var(--feedback-warning)", fontWeight: 700, marginTop: 4 }}>
                {pointsAwarded > 0 && `+${pointsAwarded} pts`}
                {pointsAwarded > 0 && recordsLabel && " · "}
                {recordsLabel && <span><FontAwesomeIcon icon={faTrophy} aria-hidden="true" /> {recordsLabel}</span>}
              </div>
            )}
          </div>

          <WeekStrip week={week} blue={blue} blueOn={blueOn} accentRgb={accentRgb} />

          {/* Stats grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { label: "Duração",      value: durStr,                                    icon: faStopwatch    },
              { label: "Exercícios",   value: totalExercises,                             icon: faDumbbell     },
              { label: "Séries",       value: totalSets,                                  icon: faLayerGroup   },
              { label: "Volume total", value: formatSummaryVolume(totalVolume), icon: faScaleBalanced, trend: volumeTrend },
            ].map(({ label, value, icon, trend }, idx) => (
              <div
                key={label}
                style={{
                  background: "rgba(" + accentRgb + ",0.08)",
                  border: "1px solid rgba(" + accentRgb + ",0.18)",
                  borderRadius: 12,
                  padding: "14px 8px 12px",
                  textAlign: "center",
                  animation: `summary-stat-in 0.32s ease ${0.18 + idx * 0.07}s both`,
                }}
              >
                <FontAwesomeIcon icon={icon} style={{ fontSize: 15, color: blue, opacity: 0.85 }} aria-hidden="true" />
                <div style={{ fontSize: 26, lineHeight: 1.15, fontWeight: 800, color: blue, marginTop: 4, whiteSpace: "nowrap" }}>{value}</div>
                <div style={{ fontSize: 10, color: "var(--text-0-50)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 3 }}>{label}</div>
                {/* Contexto do volume: comparado com a última vez deste mesmo
                    treino. Queda em cinza, não em vermelho: é o resumo que o
                    aluno posta, não um alerta. */}
                {trend && (
                  <div style={{ fontSize: 11, fontWeight: 700, marginTop: 3, color: trend.pct > 0 ? blue : "var(--text-0-60)" }}>
                    {trend.label}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Aviso "não contou para o desafio" não aparece aqui: é a única
              coisa negativa da tela e ia parar no print que o aluno posta.
              Vira um toast quando o resumo fecha (Dashboard.js). */}

          {/* Exercícios: recolhida mostra os de maior volume, sem kg por
              exercício e sem a cor de destaque (que fica só nos números). */}
          {visibleExercises.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-0-50)", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>Exercícios</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {visibleExercises.map((ex, i) => (
                  <div
                    key={`${ex.name}-${i}`}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                      padding: "7px 10px",
                      background: "var(--surface-0-60)",
                      borderRadius: 8,
                      fontSize: 13,
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      {ex.pr && <FontAwesomeIcon icon={faTrophy} style={{ color: "var(--feedback-warning)", fontSize: 12, flexShrink: 0 }} aria-label="Recorde pessoal" />}
                      <span style={{ color: "var(--text-primary)", fontWeight: ex.pr ? 700 : 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ex.name}</span>
                    </span>
                    {ex.pr ? (
                      // 1RM é estimado: o prefixo evita parecer que a pessoa levantou esse peso
                      <span style={{ color: "var(--feedback-warning)", fontWeight: 800, whiteSpace: "nowrap" }}>
                        {ex.pr.kind === "weight" ? "" : "1RM "}{fmtKg(ex.pr.value)} kg
                        <span style={{ fontWeight: 700, opacity: 0.75, marginLeft: 5 }}>▲{fmtKg(ex.pr.value - ex.pr.previous)}</span>
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-0-60)", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {ex.sets} {ex.sets === 1 ? "série" : "séries"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllExercises((v) => !v)}
                  aria-expanded={showAllExercises}
                  style={{
                    marginTop: 6, padding: "6px 4px",
                    background: "none", border: "none",
                    color: "var(--text-0-60)", fontSize: 12, fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {showAllExercises
                    ? "Mostrar menos"
                    : `+ ${hiddenCount} ${hiddenCount === 1 ? "exercício" : "exercícios"}`}
                </button>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            style={{
              width: "100%",
              padding: "13px",
              borderRadius: 12,
              background: "linear-gradient(135deg, " + blue + ", " + blueHover + ")",
              color: blueOn,
              fontWeight: 700,
              fontSize: 15,
              border: "none",
              cursor: "pointer",
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
